import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, ExternalLink, Pencil, Play, ScrollText, Square, Trash2 } from 'lucide-react';
import Tooltip from '../../ui/Tooltip';
import RunLogView from './RunLogView';
import { openPreview } from './runApi';
import { commandLine } from './runApproval';
import { isLive, runKey, stopRun, useRunStore } from './runStore';
import { errorText } from './runErrors';
import { ShareButton, ShareCard } from './share/ShareControls';
import RunFailureBanner, { failedRun } from './doctor/RunFailureBanner';
import OpenInTerminalButton from './OpenInTerminalButton';

export function statusText(t, run) {
  if (!run) return t('projects.run.status.idle');
  if (run.task) {
    if (isLive(run)) return t('projects.run.status.task', { label: run.task });
    if (run.status === 'exited' && run.exitCode === 0) return t('projects.run.status.taskDone', { label: run.task });
    if (run.status === 'exited' && run.exitCode != null) return t('projects.run.status.taskFailed', { label: run.task, code: run.exitCode });
  }
  if (run.status === 'exited' && run.exitCode != null) return t('projects.run.status.exitedCode', { code: run.exitCode });
  if (run.status === 'error') return errorText(t, run.error);
  if (run.status === 'running' && run.portTimedOut) return t('projects.run.status.portTimeout', { port: run.port });
  return t(`projects.run.status.${run.status}`);
}

export function IconButton({ label, onClick, children, danger, testId, disabled }) {
  return (
    <Tooltip content={label} side="bottom">
      <button
        type="button"
        className={`graph-icon-btn pj-run-btn${danger ? ' pj-danger' : ''}`}
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
      >
        {children}
      </button>
    </Tooltip>
  );
}

export default function RunRow({
  repo,
  config,
  logsOpen,
  onToggleLogs,
  onRun,
  onUseAnotherPort,
  onEdit,
  onDelete,
  readOnlyFile,
  report,
  onInstall,
  onCheck,
  onOpenFile,
  onFailed,
  projectName,
}) {
  const { t } = useTranslation();
  const key = runKey(repo.path, config.name);
  // Only this row re-renders on this run's output.
  const run = useRunStore((s) => s.runs[key]);
  const live = isLive(run);
  const portTaken = run?.status === 'error' && run?.error === 'port_in_use';
  // A run started on another port ("Use another port") shows and previews that one.
  const port = ((live || portTaken) && run?.port) || config.port;
  const canPreview = !run?.task && !!port && (run?.status === 'ready' || portTaken || (live && run?.status !== 'stopping'));
  const failed = failedRun(run);
  // A failed run re-checks the setup, so the banner maps to the current report.
  useEffect(() => {
    if (failed) onFailed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per failure
  }, [failed, run?.attempt]);
  return (
    <li className="pj-run-item" data-testid="run-row" data-status={run?.status || 'idle'}>
      <div className="pj-row pj-run-row">
        <span className={`pj-run-dot pj-run-dot--${run?.status || 'idle'}`} aria-hidden />
        <span className="pj-result-text pj-run-text">
          <span className="pj-result-title">
            {config.name}
            {port && <span className="pj-run-port">:{port}</span>}
          </span>
          <span className="pj-result-meta pj-run-cmd">{commandLine(config)}</span>
          <span className={`pj-result-meta pj-run-status${run?.status === 'error' || failed ? ' pj-run-status--error' : ''}`} data-testid="run-status">
            {statusText(t, run)}
          </span>
        </span>
        <div className="pj-run-actions">
          {live ? (
            <IconButton label={t('projects.run.stop')} onClick={() => stopRun(key)} testId="run-stop" disabled={run.status === 'stopping'}>
              <Square size={13} />
            </IconButton>
          ) : (
            <IconButton label={t('projects.run.start')} onClick={() => onRun([config])} testId="run-start">
              <Play size={13} />
            </IconButton>
          )}
          {canPreview && (
            <button type="button" className="gd-btn" data-testid="run-preview" onClick={() => openPreview(port, '/', config.name).catch(() => {})}>
              <ExternalLink size={13} /> {t('projects.run.openPreview')}
            </button>
          )}
          <ShareButton runKey={key} run={run} config={config} />
          {portTaken && (
            <button type="button" className="gd-btn" data-testid="run-other-port" onClick={() => onUseAnotherPort(config)}>
              <ArrowRightLeft size={13} /> {t('projects.run.useAnotherPort')}
            </button>
          )}
          {config.cwd && <OpenInTerminalButton repoPath={repo.path} cwd={config.cwd} projectName={projectName} testId="run-row-terminal" />}
          <IconButton label={logsOpen ? t('projects.run.hideLogs') : t('projects.run.showLogs')} onClick={onToggleLogs} testId="run-logs">
            <ScrollText size={13} />
          </IconButton>
          {/* Renaming or deleting a running configuration would orphan its process
              (runs are keyed by name): stop it first. */}
          <IconButton
            label={live ? t('projects.run.stopToEdit') : readOnlyFile ? t('projects.run.editCopy') : t('projects.run.edit')}
            onClick={onEdit}
            testId="run-edit"
            disabled={live}
          >
            <Pencil size={13} />
          </IconButton>
          <IconButton label={live ? t('projects.run.stopToEdit') : t('projects.run.delete')} onClick={onDelete} danger testId="run-delete" disabled={live}>
            <Trash2 size={13} />
          </IconButton>
        </div>
      </div>
      {failed && <RunFailureBanner run={run} config={config} report={report} onInstall={onInstall} onCheck={onCheck} onOpenFile={onOpenFile} />}
      <ShareCard runKey={key} run={run} />
      {(logsOpen || (run?.task && live)) && <RunLogView log={run?.log} />}
    </li>
  );
}
